import { supabase } from "@/lib/supabase";
import { formatDisplayReference, formatLeadReference, type Opportunity, type OpportunityStage } from "@/data/opportunityTypes";
import { QuoteService } from "@/services/quote.service";
import { LeadService } from "@/services/lead.service";
import { AppointmentResultService } from "@/services/appointment-result.service";
import { fetchProfileDisplayNames } from "@/lib/profileLookupCache";
import type { DbAppointmentResult } from "@/types/database";

export type AppointmentPageTab = "upcoming" | "completed";

export interface AppointmentPageQuery {
  tab: AppointmentPageTab;
  page: number;
  pageSize: number;
  search?: string;
  rep?: string;
}

export interface AppointmentPageSummary {
  total: number;
  upcomingCount: number;
  completedCount: number;
  todayCount: number;
  tomorrowCount: number;
  soldCount: number;
  noShowCount: number;
  needsOutcome: number;
  highValue: number;
  closeRate: number;
  showRate: number;
}

export interface AppointmentPageResponse {
  data: Opportunity[];
  count: number;
  source: "view" | "fallback";
  error: unknown | null;
}

export interface AppointmentSummaryResponse {
  data: AppointmentPageSummary | null;
  source: "view" | "fallback";
  error: unknown | null;
}

export interface AppointmentRepOptionsResponse {
  data: string[];
  source: "view" | "fallback";
  error: unknown | null;
}

type AppointmentUiRow = {
  opportunity_id?: string | null;
  source_kind?: string | null;
  organization_id?: string | null;
  customer_id?: string | null;
  quote_id?: string | null;
  lead_id?: string | null;
  display_reference?: string | null;
  primary_name?: string | null;
  secondary_name?: string | null;
  primary_phone?: string | null;
  secondary_phone?: string | null;
  primary_email?: string | null;
  secondary_email?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  project_type?: string | null;
  lead_source?: string | null;
  lead_cost?: number | null;
  stage?: string | null;
  appointment_time?: string | null;
  assigned_rep?: string | null;
  assigned_rep_name?: string | null;
  total_estimate?: number | null;
  linked_job_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  notes?: string | null;
  won_at?: string | null;
  lost_at?: string | null;
  lost_reason?: string | null;
  outcome?: string | null;
  appointment_bucket?: AppointmentPageTab | null;
  sort_at?: string | null;
};

type HydrationRow = Record<string, unknown> & {
  id?: string;
  lead_id?: string;
  quote_number?: string;
  displayReference?: string;
  primary_name?: string;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  customer_address?: string;
  address?: string;
  address_line1?: string;
  city?: string;
  state?: string;
  zip?: string;
  postal_code?: string;
  project_type?: string;
  appointment_time?: string;
  scheduled_at?: string;
  assigned_rep_name?: string;
  assigned_rep?: string;
  first_name?: string;
  last_name?: string;
  secondary_name?: string;
  primary_phone?: string;
  phone?: string;
  secondary_phone?: string;
  primary_email?: string;
  email?: string;
  secondary_email?: string;
  lead_source?: string;
  source_name?: string;
  stage?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  notes?: string;
  customer_id?: string;
  organization_id?: string;
  lead_cost?: number | null;
  total_estimate?: number | null;
  linked_job_id?: string | null;
  linked_quote_id?: string | null;
};

const APPOINTMENTS_VIEW_COLUMNS = [
  "opportunity_id",
  "source_kind",
  "organization_id",
  "customer_id",
  "quote_id",
  "lead_id",
  "display_reference",
  "primary_name",
  "secondary_name",
  "primary_phone",
  "secondary_phone",
  "primary_email",
  "secondary_email",
  "address",
  "city",
  "state",
  "zip",
  "project_type",
  "lead_source",
  "lead_cost",
  "stage",
  "appointment_time",
  "assigned_rep",
  "assigned_rep_name",
  "total_estimate",
  "linked_job_id",
  "created_at",
  "updated_at",
  "notes",
  "won_at",
  "lost_at",
  "lost_reason",
  "appointment_bucket",
  "outcome",
  "sort_at",
].join(", ");

const SUMMARY_COLUMNS = [
  "appointment_bucket",
  "outcome",
  "appointment_time",
  "total_estimate",
].join(", ");

const DEFAULT_PROJECT_TYPE = "Other";

function isUuidLike(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
}

function toStage(value: unknown): OpportunityStage {
  const stage = String(value ?? "").trim();
  if (
    stage === "Lead" ||
    stage === "Quote Created" ||
    stage === "Appointment Scheduled" ||
    stage === "Appointment Completed" ||
    stage === "Follow-Up Needed" ||
    stage === "Closed Won" ||
    stage === "Closed Lost"
  ) {
    return stage;
  }
  return "Lead";
}

function firstNonEmpty(values: unknown[]): string {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
}

function mapRowToOpportunity(row: AppointmentUiRow): Opportunity {
  const id = String(row.opportunity_id ?? row.quote_id ?? row.lead_id ?? "");
  const leadId = String(row.lead_id ?? "").trim();
  const quoteId = String(row.quote_id ?? "").trim();
  const displayReference = row.display_reference
    ? formatDisplayReference(String(row.display_reference))
    : leadId
      ? formatLeadReference(leadId)
      : id;
  const assignedRep = firstNonEmpty([row.assigned_rep_name, row.assigned_rep]);
  const stage = toStage(row.stage);
  const createdAt = String(row.created_at ?? new Date().toISOString());
  const updatedAt = String(row.updated_at ?? createdAt);

  return {
    id,
    quoteId: quoteId || null,
    leadId: leadId || null,
    displayReference,
    primaryName: String(row.primary_name ?? "Unknown Customer"),
    secondaryName: String(row.secondary_name ?? ""),
    primaryPhone: String(row.primary_phone ?? ""),
    secondaryPhone: String(row.secondary_phone ?? ""),
    primaryEmail: String(row.primary_email ?? ""),
    secondaryEmail: String(row.secondary_email ?? ""),
    address: String(row.address ?? ""),
    city: String(row.city ?? ""),
    state: String(row.state ?? ""),
    zip: String(row.zip ?? ""),
    projectType: String(row.project_type ?? DEFAULT_PROJECT_TYPE) as Opportunity["projectType"],
    leadSource: String(row.lead_source ?? ""),
    leadCost: Number(row.lead_cost ?? 0) || undefined,
    stage,
    stageHistory: [],
    assignedRep: assignedRep && !isUuidLike(assignedRep) ? assignedRep : String(row.assigned_rep ?? ""),
    ownershipHistory: [],
    activities: [],
    appointmentTime: row.appointment_time ?? undefined,
    totalEstimate: Number(row.total_estimate ?? 0),
    linkedJobId: row.linked_job_id ?? undefined,
    createdAt,
    updatedAt,
    notes: String(row.notes ?? ""),
    wonAt: row.won_at ?? undefined,
    lostAt: row.lost_at ?? undefined,
    lostReason: row.lost_reason ?? undefined,
    customerId: row.customer_id ?? undefined,
    organizationId: row.organization_id ?? undefined,
    customerName: String(row.primary_name ?? "Unknown Customer"),
    customerPhone: String(row.primary_phone ?? ""),
    customerEmail: String(row.primary_email ?? ""),
    customerAddress: [row.address, row.city, [row.state, row.zip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
    createdBy: assignedRep,
    status: stage,
    quoteName: "",
    quote_number: displayReference,
    convertedJobId: row.linked_job_id ?? undefined,
    appointmentId: id,
    linkedQuoteId: quoteId || undefined,
  } as Opportunity;
}

function normalizeHydrationOpportunity(candidate: HydrationRow): Opportunity {
  const fallbackName = [candidate.first_name, candidate.last_name]
    .map((v) => String(v ?? "").trim())
    .filter(Boolean)
    .join(" ");
  const primaryName = firstNonEmpty([
    candidate.primaryName,
    candidate.customerName,
    candidate.customer_name,
    candidate.primary_name,
    fallbackName,
  ]) || "Unknown Customer";
  const leadId = firstNonEmpty([candidate.leadId, candidate.lead_id]);
  const id = String(candidate.id ?? candidate.quoteId ?? leadId ?? `OPP-${Date.now()}`);
  const rawDisplayReference = firstNonEmpty([
    candidate.displayReference,
    candidate.display_reference,
    candidate.quote_number,
    candidate.linked_quote_id,
  ]);
  const displayReference = rawDisplayReference
    ? (isUuidLike(rawDisplayReference) ? (leadId ? formatLeadReference(leadId) : "OPP-UNASSIGNED") : formatDisplayReference(rawDisplayReference))
    : (leadId ? formatLeadReference(leadId) : (isUuidLike(id) ? "OPP-UNASSIGNED" : formatDisplayReference(id)));
  const assignedRepRaw = firstNonEmpty([
    candidate.assigned_rep_name,
    candidate.assignedRep,
    candidate.createdBy,
    candidate.created_by,
    candidate.assigned_rep,
  ]);
  const assignedRep = assignedRepRaw && !isUuidLike(assignedRepRaw) ? assignedRepRaw : String(candidate.assigned_rep ?? "");
  const city = String(candidate.city ?? "").trim();
  const state = String(candidate.state ?? "").trim();
  const zip = String(candidate.zip ?? candidate.postal_code ?? "").trim();
  const street = String(candidate.address ?? candidate.address_line1 ?? "").trim();
  const createdAt = String(candidate.createdAt ?? candidate.created_at ?? new Date().toISOString());
  const updatedAt = String(candidate.updatedAt ?? candidate.updated_at ?? createdAt);
  const stage = toStage(candidate.stage ?? candidate.status);

  return {
    id,
    quoteId: candidate.quoteId ?? null,
    leadId: leadId || null,
    displayReference,
    primaryName,
    secondaryName: String(candidate.secondaryName ?? candidate.secondary_name ?? ""),
    primaryPhone: String(candidate.primaryPhone ?? candidate.customer_phone ?? candidate.primary_phone ?? candidate.phone ?? ""),
    secondaryPhone: String(candidate.secondaryPhone ?? candidate.secondary_phone ?? ""),
    primaryEmail: String(candidate.primaryEmail ?? candidate.customer_email ?? candidate.primary_email ?? candidate.email ?? ""),
    secondaryEmail: String(candidate.secondaryEmail ?? candidate.secondary_email ?? ""),
    address: street,
    city,
    state,
    zip,
    projectType: String(candidate.projectType ?? candidate.project_type ?? DEFAULT_PROJECT_TYPE) as Opportunity["projectType"],
    leadSource: String(candidate.leadSource ?? candidate.lead_source ?? candidate.source_name ?? ""),
    leadCost: Number(candidate.leadCost ?? candidate.lead_cost ?? 0) || undefined,
    stage,
    stageHistory: Array.isArray(candidate.stageHistory)
      ? candidate.stageHistory as any[]
      : (Array.isArray(candidate.stage_history) ? candidate.stage_history as any[] : []),
    assignedRep,
    ownershipHistory: [],
    activities: Array.isArray(candidate.activities) ? candidate.activities as any[] : [],
    appointmentTime: typeof candidate.appointment_time === "string"
      ? candidate.appointment_time
      : (typeof candidate.scheduled_at === "string" ? candidate.scheduled_at : undefined),
    totalEstimate: Number(candidate.totalEstimate ?? candidate.total_estimate ?? 0),
    linkedJobId: candidate.linked_job_id ?? candidate.convertedJobId as string | undefined,
    createdAt,
    updatedAt,
    notes: String(candidate.notes ?? ""),
    customerId: candidate.customer_id ?? undefined,
    organizationId: candidate.organization_id ?? undefined,
    customerName: primaryName,
    customerPhone: String(candidate.primaryPhone ?? candidate.customer_phone ?? candidate.primary_phone ?? candidate.phone ?? ""),
    customerEmail: String(candidate.primaryEmail ?? candidate.customer_email ?? candidate.primary_email ?? candidate.email ?? ""),
    customerAddress: [street, city, [state, zip].filter(Boolean).join(" ")].filter(Boolean).join(", "),
    createdBy: assignedRep,
    status: stage,
    quoteName: String(candidate.quoteName ?? ""),
    quote_number: String(candidate.quote_number ?? ""),
    convertedJobId: candidate.linked_job_id as string | undefined,
    appointmentId: id,
    linkedQuoteId: candidate.linked_quote_id as string | undefined,
  } as Opportunity;
}

function deriveOutcome(opportunity: Opportunity, appointmentResult?: DbAppointmentResult | null): "upcoming" | "needs_outcome" | "sold" | "no_sale" | "no_show" | "canceled" {
  if (opportunity.linkedJobId || opportunity.stage === "Closed Won" || appointmentResult?.result === "booked") return "sold";
  if (appointmentResult?.status === "no_show") return "no_show";
  if (appointmentResult?.status === "cancelled") return "canceled";
  if (opportunity.stage === "Closed Lost" || opportunity.stage === "Follow-Up Needed" || opportunity.stage === "Appointment Completed") return "no_sale";
  if (opportunity.appointmentTime) {
    const appointmentMs = new Date(opportunity.appointmentTime).getTime();
    if (Number.isFinite(appointmentMs) && appointmentMs < Date.now()) return "needs_outcome";
  }
  return "upcoming";
}

function bucketForOpportunity(opportunity: Opportunity, appointmentResult?: DbAppointmentResult | null): AppointmentPageTab {
  const outcome = deriveOutcome(opportunity, appointmentResult);
  return outcome === "upcoming" || outcome === "needs_outcome" ? "upcoming" : "completed";
}

function applyLocalFilters(rows: Opportunity[], query: AppointmentPageQuery): Opportunity[] {
  const search = String(query.search ?? "").trim().toLowerCase();
  const rep = String(query.rep ?? "").trim().toLowerCase();
  return rows.filter((row) => {
    if (query.tab !== bucketForOpportunity(row)) return false;
    if (rep && String(row.assignedRep ?? "").trim().toLowerCase() !== rep) return false;
    if (!search) return true;
    return [row.primaryName, row.projectType, row.assignedRep, row.leadSource]
      .some((value) => String(value ?? "").toLowerCase().includes(search));
  });
}

function sortForTab(rows: Opportunity[], tab: AppointmentPageTab): Opportunity[] {
  return [...rows].sort((a, b) => {
    const aTime = new Date(a.appointmentTime || a.updatedAt || a.createdAt).getTime();
    const bTime = new Date(b.appointmentTime || b.updatedAt || b.createdAt).getTime();
    return tab === "upcoming" ? aTime - bTime : bTime - aTime;
  });
}

function summarize(rows: Opportunity[]): AppointmentPageSummary {
  const today = new Date();
  const todayKey = today.toDateString();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const tomorrowKey = tomorrow.toDateString();
  let upcomingCount = 0;
  let completedCount = 0;
  let todayCount = 0;
  let tomorrowCount = 0;
  let soldCount = 0;
  let noShowCount = 0;
  let needsOutcome = 0;
  let highValue = 0;

  for (const row of rows) {
    const outcome = deriveOutcome(row);
    const bucket = bucketForOpportunity(row);
    if (bucket === "upcoming") upcomingCount += 1;
    if (bucket === "completed") completedCount += 1;
    if (outcome === "sold") soldCount += 1;
    if (outcome === "no_show") noShowCount += 1;
    if (outcome === "needs_outcome") needsOutcome += 1;
    if (bucket === "upcoming" && (row.totalEstimate || 0) >= 15_000) highValue += 1;
    if (row.appointmentTime) {
      const appointmentDate = new Date(row.appointmentTime);
      if (!Number.isNaN(appointmentDate.getTime())) {
        if (appointmentDate.toDateString() === todayKey) todayCount += 1;
        if (appointmentDate.toDateString() === tomorrowKey) tomorrowCount += 1;
      }
    }
  }

  return {
    total: rows.length,
    upcomingCount,
    completedCount,
    todayCount,
    tomorrowCount,
    soldCount,
    noShowCount,
    needsOutcome,
    highValue,
    closeRate: completedCount > 0 ? Math.round((soldCount / completedCount) * 100) : 0,
    showRate: completedCount > 0 ? Math.round(((completedCount - noShowCount) / completedCount) * 100) : 100,
  };
}

function summarizeViewRows(rows: AppointmentUiRow[]): AppointmentPageSummary {
  const today = new Date();
  const todayKey = today.toDateString();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const tomorrowKey = tomorrow.toDateString();
  let upcomingCount = 0;
  let completedCount = 0;
  let todayCount = 0;
  let tomorrowCount = 0;
  let soldCount = 0;
  let noShowCount = 0;
  let needsOutcome = 0;
  let highValue = 0;

  for (const row of rows) {
    const outcome = String(row.outcome ?? "");
    const bucket = row.appointment_bucket;
    if (bucket === "upcoming") upcomingCount += 1;
    if (bucket === "completed") completedCount += 1;
    if (outcome === "sold") soldCount += 1;
    if (outcome === "no_show") noShowCount += 1;
    if (outcome === "needs_outcome") needsOutcome += 1;
    if (bucket === "upcoming" && Number(row.total_estimate ?? 0) >= 15_000) highValue += 1;
    if (row.appointment_time) {
      const appointmentDate = new Date(row.appointment_time);
      if (!Number.isNaN(appointmentDate.getTime())) {
        if (appointmentDate.toDateString() === todayKey) todayCount += 1;
        if (appointmentDate.toDateString() === tomorrowKey) tomorrowCount += 1;
      }
    }
  }

  return {
    total: rows.length,
    upcomingCount,
    completedCount,
    todayCount,
    tomorrowCount,
    soldCount,
    noShowCount,
    needsOutcome,
    highValue,
    closeRate: completedCount > 0 ? Math.round((soldCount / completedCount) * 100) : 0,
    showRate: completedCount > 0 ? Math.round(((completedCount - noShowCount) / completedCount) * 100) : 100,
  };
}

async function getFallbackOpportunities(): Promise<Opportunity[]> {
  const [quotesRes, leadsRes, appointmentResultsRes] = await Promise.all([
    QuoteService.getOpportunityHydrationRows(),
    LeadService.getAll(),
    AppointmentResultService.getAll(),
  ]);
  const quoteRows = (quotesRes.data ?? []) as HydrationRow[];
  const leadRows = (leadsRes.data ?? []) as HydrationRow[];
  const appointmentRows = (appointmentResultsRes.data ?? []) as DbAppointmentResult[];

  const latestApptByLeadId = new Map<string, string>();
  for (const row of appointmentRows) {
    const leadId = String(row.lead_id ?? "").trim();
    const scheduledAt = String(row.scheduled_at ?? "").trim();
    if (!leadId || !scheduledAt) continue;
    const current = latestApptByLeadId.get(leadId);
    if (!current || new Date(scheduledAt).getTime() > new Date(current).getTime()) {
      latestApptByLeadId.set(leadId, scheduledAt);
    }
  }

  const assignedRepIds = Array.from(
    new Set(
      [...quoteRows, ...leadRows]
        .map((row) => row.assigned_rep)
        .filter((value): value is string => typeof value === "string" && value.trim().length > 0),
    ),
  );
  const repNameById = assignedRepIds.length > 0 ? await fetchProfileDisplayNames(assignedRepIds) : new Map<string, string>();
  const leadById = new Map<string, HydrationRow>(leadRows.map((lead) => [String(lead.id ?? ""), lead]));

  const quoteOpps = quoteRows.map((quote) => {
    const leadId = String(quote.lead_id ?? "");
    const lead = leadById.get(leadId);
    const assignedRep = String(quote.assigned_rep ?? lead?.assigned_rep ?? "");
    return normalizeHydrationOpportunity({
      ...lead,
      ...quote,
      id: quote.id,
      quoteId: quote.id,
      leadId: leadId || undefined,
      displayReference: quote.displayReference ?? quote.quote_number ?? quote.id,
      primary_name: quote.primary_name ?? quote.customer_name ?? lead?.primary_name,
      primary_phone: quote.primary_phone ?? quote.customer_phone ?? lead?.primary_phone,
      primary_email: quote.primary_email ?? quote.customer_email ?? lead?.primary_email,
      address: quote.address ?? lead?.address ?? lead?.address_line1,
      city: quote.city ?? lead?.city,
      state: quote.state ?? lead?.state,
      zip: quote.zip ?? quote.postal_code ?? lead?.zip ?? lead?.postal_code,
      project_type: quote.project_type ?? lead?.project_type,
      stage: quote.stage ?? lead?.stage,
      appointment_time: quote.appointment_time ?? lead?.appointment_time ?? latestApptByLeadId.get(leadId),
      scheduled_at: latestApptByLeadId.get(leadId),
      assigned_rep: assignedRep,
      assigned_rep_name: assignedRep ? repNameById.get(assignedRep) ?? quote.assigned_rep_name ?? lead?.assigned_rep_name : undefined,
      stage_history: lead?.stage_history ?? quote.stage_history,
      activities: lead?.activities ?? quote.activities,
    });
  });

  const quoteLeadIds = new Set(quoteRows.map((quote) => String(quote.lead_id ?? "")).filter(Boolean));
  const leadOpps = leadRows
    .filter((lead) => !quoteLeadIds.has(String(lead.id ?? "")))
    .map((lead) => {
      const assignedRep = String(lead.assigned_rep ?? "");
      return normalizeHydrationOpportunity({
        ...lead,
        id: String(lead.id ?? ""),
        displayReference: formatLeadReference(String(lead.id ?? "")),
        leadId: String(lead.id ?? ""),
        assigned_rep_name: assignedRep ? repNameById.get(assignedRep) ?? lead.assigned_rep_name : undefined,
        appointment_time: lead.appointment_time ?? latestApptByLeadId.get(String(lead.id ?? "")),
      });
    });

  return [...quoteOpps, ...leadOpps].filter((row) => row.appointmentTime || row.stage !== "Lead");
}

function applyViewFilters<T extends any>(query: T, params: Pick<AppointmentPageQuery, "search" | "rep">): T {
  let next = query as any;
  const search = String(params.search ?? "").trim();
  if (search) {
    const term = `%${search.replace(/[%_]/g, "").replace(/,/g, " ")}%`;
    next = next.or(`primary_name.ilike.${term},project_type.ilike.${term},assigned_rep_name.ilike.${term},lead_source.ilike.${term}`);
  }

  const rep = String(params.rep ?? "").trim();
  if (rep) {
    if (isUuidLike(rep)) {
      next = next.eq("assigned_rep", rep);
    } else {
      next = next.ilike("assigned_rep_name", rep);
    }
  }
  return next as T;
}

export const AppointmentPageService = {
  async getPage(query: AppointmentPageQuery): Promise<AppointmentPageResponse> {
    const page = Math.max(0, query.page || 0);
    const pageSize = Math.max(1, Math.min(query.pageSize || 20, 100));
    const from = page * pageSize;
    const to = from + pageSize - 1;

    try {
      let request = supabase
        .from("v_appointments_ui")
        .select(APPOINTMENTS_VIEW_COLUMNS, { count: "exact" })
        .eq("appointment_bucket", query.tab)
        .order("sort_at", { ascending: query.tab === "upcoming", nullsFirst: false })
        .range(from, to);
      request = applyViewFilters(request, query);
      const { data, error, count } = await request;
      if (error) throw error;
      return {
        data: ((data ?? []) as AppointmentUiRow[]).map(mapRowToOpportunity),
        count: count ?? 0,
        source: "view",
        error: null,
      };
    } catch (error) {
      console.warn("AppointmentPageService.getPage falling back to client hydration:", error);
      const allRows = await getFallbackOpportunities();
      const filtered = sortForTab(applyLocalFilters(allRows, query), query.tab);
      return {
        data: filtered.slice(from, to + 1),
        count: filtered.length,
        source: "fallback",
        error,
      };
    }
  },

  async getSummary(filters?: Pick<AppointmentPageQuery, "search" | "rep">): Promise<AppointmentSummaryResponse> {
    try {
      let request = supabase
        .from("v_appointments_ui")
        .select(SUMMARY_COLUMNS);
      request = applyViewFilters(request, filters ?? {});
      const { data, error } = await request;
      if (error) throw error;
      return { data: summarizeViewRows((data ?? []) as AppointmentUiRow[]), source: "view", error: null };
    } catch (error) {
      console.warn("AppointmentPageService.getSummary falling back to client hydration:", error);
      const allRows = await getFallbackOpportunities();
      const filtered = applyLocalFilters(allRows, { tab: "upcoming", page: 0, pageSize: allRows.length || 1, ...(filters ?? {}) })
        .concat(applyLocalFilters(allRows, { tab: "completed", page: 0, pageSize: allRows.length || 1, ...(filters ?? {}) }));
      return { data: summarize(filtered), source: "fallback", error };
    }
  },

  async getRepOptions(): Promise<AppointmentRepOptionsResponse> {
    try {
      const { data, error } = await supabase
        .from("v_appointments_ui")
        .select("assigned_rep, assigned_rep_name")
        .not("assigned_rep", "is", null)
        .limit(1000);
      if (error) throw error;
      const reps = Array.from(
        new Set(
          ((data ?? []) as AppointmentUiRow[])
            .map((row) => firstNonEmpty([row.assigned_rep, row.assigned_rep_name]))
            .filter(Boolean),
        ),
      ).sort((a, b) => a.localeCompare(b));
      return { data: reps, source: "view", error: null };
    } catch (error) {
      console.warn("AppointmentPageService.getRepOptions falling back to client hydration:", error);
      const allRows = await getFallbackOpportunities();
      const reps = Array.from(
        new Set(
          allRows
            .map((row) => String(row.assignedRep ?? "").trim())
            .filter(Boolean),
        ),
      ).sort((a, b) => a.localeCompare(b));
      return { data: reps, source: "fallback", error };
    }
  },
};
